const formatCompactDate = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const normalized = value.replace(/-/g, "").trim();
  if (!/^\d{8}$/.test(normalized)) {
    return value.trim();
  }

  const year = normalized.slice(0, 4);
  const month = normalized.slice(4, 6);
  const day = normalized.slice(6, 8);
  const parsedDate = new Date(`${year}-${month}-${day}T00:00:00Z`);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalized;
  }

  return parsedDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const buildSubmissionSummaryLine = (submission) => {
  if (!submission || typeof submission !== "object") {
    return "";
  }

  const status = submission.submission_status?.trim();
  const formattedDate = formatCompactDate(submission.submission_status_date);
  const priority = submission.review_priority?.trim();
  const classCode = submission.submission_class_code?.trim();

  return [
    status ? `Status: ${status}` : "",
    formattedDate ? `Date: ${formattedDate}` : "",
    priority ? `Priority: ${priority}` : "",
    classCode ? `Class: ${classCode}` : "",
  ]
    .filter(Boolean)
    .join(" | ");
};

const getNormalizedSubmissions = (value) => {
  if (!Array.isArray(value) || !value.length) {
    return [];
  }

  return value.filter((item) => item && typeof item === "object");
};

const normalizeSubmissionType = (submission) =>
  String(submission?.submission_type || "").trim().toUpperCase();

const parseSubmissionDateTime = (value) => {
  if (typeof value !== "string") {
    return 0;
  }

  const normalized = value.replace(/-/g, "").trim();
  if (!/^\d{8}$/.test(normalized)) {
    return 0;
  }

  const parsedDate = Date.UTC(
    Number(normalized.slice(0, 4)),
    Number(normalized.slice(4, 6)) - 1,
    Number(normalized.slice(6, 8))
  );
  return Number.isNaN(parsedDate) ? 0 : parsedDate;
};

const parseSubmissionNumber = (value) => {
  const parsedNumber = Number.parseInt(String(value || "").trim(), 10);
  return Number.isNaN(parsedNumber) ? 0 : parsedNumber;
};

const compareSubmissionsNewestFirst = (left, right) => {
  const dateDelta =
    parseSubmissionDateTime(right?.submission_status_date) -
    parseSubmissionDateTime(left?.submission_status_date);

  if (dateDelta !== 0) {
    return dateDelta;
  }

  return (
    parseSubmissionNumber(right?.submission_number) -
    parseSubmissionNumber(left?.submission_number)
  );
};

export const getDrugSubmissionBadgeTier = (submission) => {
  const submissionType = normalizeSubmissionType(submission);
  const classCode = String(submission?.submission_class_code || "").trim().toUpperCase();
  const description = String(submission?.submission_class_code_description || "").trim();
  const normalizedDescription = description.toLowerCase();
  const combined = `${submissionType} ${classCode} ${normalizedDescription}`;

  if (submissionType === "ORIG") {
    return { tier: "original", label: "Original", code: "ORIG" };
  }

  if (classCode.includes("EFFICACY") || normalizedDescription.includes("efficacy")) {
    return { tier: "high", label: "Efficacy", code: classCode || "EFFICACY" };
  }

  if (classCode.includes("LABELING") || normalizedDescription.includes("labeling")) {
    return { tier: "high", label: "Labeling", code: classCode || "LABELING" };
  }

  if (
    classCode.includes("MANUF") ||
    classCode.includes("CMC") ||
    normalizedDescription.includes("manufacturing")
  ) {
    return { tier: "high", label: "Manufacturing", code: classCode || "MANUF" };
  }

  if (
    combined.includes("annual report") ||
    combined.includes("postmarketing") ||
    combined.includes("post-marketing") ||
    combined.includes("commitment") ||
    combined.includes("requirement") ||
    combined.includes("minor")
  ) {
    return { tier: "low", label: "Lower Priority", code: classCode || submissionType || "N/A" };
  }

  return {
    tier: "neutral",
    label: classCode || description || submissionType || "N/A",
    code: classCode || submissionType || "N/A",
  };
};

export const getDrugSubmissionDisplayModel = (_value, record = {}) => {
  const normalizedSubmissions = getNormalizedSubmissions(record.submissions);
  const originalSubmission =
    record.original_submission && typeof record.original_submission === "object"
      ? record.original_submission
      : normalizedSubmissions.find((item) => normalizeSubmissionType(item) === "ORIG") || null;
  const supplementalSource = getNormalizedSubmissions(record.supplemental_submissions).length
    ? getNormalizedSubmissions(record.supplemental_submissions)
    : normalizedSubmissions.filter((item) => normalizeSubmissionType(item) !== "ORIG");
  const supplementalSubmissions = [...supplementalSource]
    .sort(compareSubmissionsNewestFirst)
    .map((submission) => ({
      ...submission,
      badge: getDrugSubmissionBadgeTier(submission),
    }));

  if (!originalSubmission && !supplementalSubmissions.length) {
    return null;
  }

  return {
    originalSubmission: originalSubmission
      ? {
          ...originalSubmission,
          badge: getDrugSubmissionBadgeTier(originalSubmission),
        }
      : null,
    supplementalSubmissions,
  };
};

export const formatOriginalSubmission = (value) => {
  const normalizedSubmissions = getNormalizedSubmissions(value);

  if (!normalizedSubmissions.length) {
    return "";
  }

  const originalSubmission =
    normalizedSubmissions.find(
      (item) => String(item.submission_type || "").trim().toUpperCase() === "ORIG"
    ) || null;

  if (!originalSubmission) {
    return "";
  }

  return buildSubmissionSummaryLine(originalSubmission);
};

export const formatSupplementalSubmissions = (value) => {
  const normalizedSubmissions = getNormalizedSubmissions(value);

  if (!normalizedSubmissions.length) {
    return "";
  }

  const supplementalSubmissions = normalizedSubmissions.filter(
    (item) => String(item.submission_type || "").trim().toUpperCase() !== "ORIG"
  );

  if (!supplementalSubmissions.length) {
    return "";
  }

  return [
    `Supplemental Submissions (${supplementalSubmissions.length})`,
    ...supplementalSubmissions.map((submission, index) => {
      const submissionType = submission.submission_type?.trim() || "SUPPL";
      const summary = buildSubmissionSummaryLine(submission);
      return `${index + 1}. ${submissionType}${summary ? `\n   ${summary}` : ""}`;
    }),
  ].join("\n");
};

export const getLocationCountries = (value) => {
  if (!Array.isArray(value) || !value.length) {
    return [];
  }

  return [
    ...new Set(
      value
        .map((item) => (item && typeof item === "object" ? item.country : null))
        .filter((country) => typeof country === "string")
        .map((country) => country.trim())
        .filter(Boolean)
    ),
  ];
};

export const getFirstSemicolonSeparatedValue = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const [firstValue] = value
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);

  return firstValue || value.trim();
};

export const getSemicolonSeparatedValues = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => getSemicolonSeparatedValues(item));
  }

  if (typeof value !== "string") {
    return value;
  }

  return value
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const getFirstArrayValue = (value) => {
  if (!Array.isArray(value)) {
    return value;
  }

  const firstValue = value.find((item) => {
    if (typeof item === "string") {
      return item.trim() !== "";
    }

    return item !== null && item !== undefined;
  });

  return firstValue ?? value[0] ?? value;
};

export const normalizePatentNumber = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value.replace(/-/g, "").trim();
};

export const buildFdaSubmissionPdfHref = (value) => {
  const normalizedValue = String(value ?? "").trim().toUpperCase();

  if (!normalizedValue) {
    return "";
  }

  const match = normalizedValue.match(/^[A-Z]+(\d{2})/);
  if (!match) {
    return "";
  }

  const [, year] = match;
  return `https://www.accessdata.fda.gov/cdrh_docs/pdf${year}/${normalizedValue}.pdf`;
};

export const formatPatentPriorityDate = (value) => {
  const normalized = String(value ?? "").replace(/-/g, "").trim();

  if (!normalized || normalized === "0") {
    return "-";
  }

  if (!/^\d{8}$/.test(normalized)) {
    return String(value ?? "").trim();
  }

  const year = normalized.slice(0, 4);
  const month = normalized.slice(4, 6);
  const day = normalized.slice(6, 8);
  const parsedDate = new Date(`${year}-${month}-${day}T00:00:00Z`);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalized;
  }

  return parsedDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const parseReviewDate = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const dateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return Date.UTC(
    parsedDate.getUTCFullYear(),
    parsedDate.getUTCMonth(),
    parsedDate.getUTCDate()
  );
};

const getReviewTimeDays = (record) => {
  const approvalDate =
    parseReviewDate(record?.approval_date) ?? parseReviewDate(record?.decision_date);
  const submissionDate =
    parseReviewDate(record?.submission_date) ??
    parseReviewDate(record?.date_received) ??
    parseReviewDate(record?.receive_date);

  if (approvalDate === null || submissionDate === null) {
    return null;
  }

  const diffInDays = Math.round((approvalDate - submissionDate) / (1000 * 60 * 60 * 24));
  return diffInDays >= 0 ? diffInDays : null;
};

export const formatReviewTime = (_value, record) => {
  const reviewTimeDays = getReviewTimeDays(record);
  if (reviewTimeDays === null) {
    return null;
  }

  return `${reviewTimeDays} days`;
};

export const reviewTimeSources = [
  "approval_date",
  "decision_date",
  "submission_date",
  "date_received",
  "receive_date",
];
